// Task List chronology (#437).
//
// The card's layout rules, as a pure function so they are unit-testable without
// a DOM or a clock:
//   * tasks created TODAY come first, with NO header;
//   * every earlier day follows, newest day first, under a thin separator that
//     carries the date as DD/MM/YYYY;
//   * inside a day, tasks keep the order they were added (oldest first), and a
//     completed task keeps its position — completion never reorders.
//
// "Today" and every day boundary are the viewer's LOCAL days in `timeZone`
// (default: the active timezone the rest of the app uses), because the server
// stores naive UTC and a task added at 23:30 in Bangkok belongs to that evening,
// not to the next UTC day.
import { parseServerDate } from './datetime';

const dayKeyFormatter = (timeZone) => {
    try {
        return new Intl.DateTimeFormat('en-CA', {
            timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
        });
    } catch {
        return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' });
    }
};

/** 'YYYY-MM-DD' of `date` in `timeZone` (sortable as a string). */
export function localDayKey(date, timeZone) {
    return dayKeyFormatter(timeZone).format(date);
}

/** 'YYYY-MM-DD' → 'DD/MM/YYYY' (the Contacts module's date order). */
export function formatDayLabel(dayKey) {
    const [y, m, d] = String(dayKey || '').split('-');
    return y && m && d ? `${d}/${m}/${y}` : '';
}

/**
 * Group tasks for display.
 * @returns {Array<{key: string, isToday: boolean, label: string|null, tasks: object[]}>}
 */
export function groupTasksByDay(tasks, { now = new Date(), timeZone } = {}) {
    const todayKey = localDayKey(now, timeZone);
    const buckets = new Map();
    const ordered = [...(tasks || [])]
        .map((t, i) => ({ t, i, when: parseServerDate(t.created_at) }))
        .sort((a, b) => {
            const ta = a.when ? a.when.getTime() : 0;
            const tb = b.when ? b.when.getTime() : 0;
            return ta - tb || a.i - b.i;
        });
    for (const { t, when } of ordered) {
        const key = when ? localDayKey(when, timeZone) : todayKey;
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(t);
    }
    return [...buckets.entries()]
        .sort(([a], [b]) => {
            if (a === todayKey) return -1;
            if (b === todayKey) return 1;
            return a < b ? 1 : a > b ? -1 : 0;
        })
        .map(([key, list]) => ({
            key,
            isToday: key === todayKey,
            label: key === todayKey ? null : formatDayLabel(key),
            tasks: list,
        }));
}
